// Presentation helpers shared by the customer search and new-customer drawers
// (v4 layout: 440px search drawer, 480px registration drawer). Pure functions
// only, so both drawers and their tests agree on what a row or hint shows.
import { toCamelCase } from "./customerFormUtils";

export const SEARCH_DRAWER_WIDTH = 440;
export const NEW_CUSTOMER_DRAWER_WIDTH = 480;
export const DRAWER_MASK = { backgroundColor: "rgba(25,34,40,.5)" };

export const initialsOf = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
};

// Loyalty tier if the customer has one, else their segment.
export const tierOf = (customer) => customer?.loyaltyLevel?.name || customer?.loyaltyLevel?.value || customer?.b2cCustomerSegment?.name || "";

export const tierTone = (tier) => (/gold|platinum|premium|vip|elite/i.test(String(tier || "")) ? "gold" : "plain");

export const searchHint = (term, count, { offline = false } = {}) => {
  const t = String(term || "").trim();
  if (offline) return "You are offline. Customer search needs an internet connection.";
  if (!t) return "Search by name, phone or email";
  if (count == null) return `Searching for "${t}"…`;
  if (count === 0) return `No customer matches "${t}"`;
  return `${count} ${count === 1 ? "match" : "matches"} for "${t}" · name, phone or email`;
};

export const digitsOf = (value) => String(value ?? "").replace(/\D/g, "");

const PHONE_KEY = /mobile|phone|contactnumber|contactno/i;

export const isPhoneFieldName = (name) => PHONE_KEY.test(String(name || "").replace(/\s+/g, ""));

// The phone number typed in the form, wherever the tenant's config put the field
// (top level or inside a group_<id> object).
export const findPhoneValue = (values) => {
  if (!values || typeof values !== "object") return "";
  for (const [key, val] of Object.entries(values)) {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const inner = findPhoneValue(val);
      if (inner) return inner;
    } else if (isPhoneFieldName(key) && digitsOf(val)) {
      return String(val);
    }
  }
  return "";
};

// A search row with the same number (last 10 digits compared, so "+91" prefixes match).
export const findSamePhone = (rows, phone) => {
  const want = digitsOf(phone).slice(-10);
  if (want.length < 10 || !Array.isArray(rows)) return null;
  return rows.find((r) => digitsOf(r?.mobileNo).slice(-10) === want) || null;
};

// Fields shown up front on a new customer; everything else (DOB, GST, address…)
// waits behind "More details". Mandatory fields are always up front.
const ESSENTIAL = /^(first ?name|last ?name|name|mobile|mobile ?no|mobile ?number|phone|phone ?number|contact ?number|contact ?no|email|e-?mail|city|pin ?code|postal ?code|zip)$/i;

export const isEssentialField = (field) => {
  if (!field) return false;
  if (field.mandatory) return true;
  return ESSENTIAL.test(String(field.name || "").trim());
};

// Whether a field is tucked behind "More details" right now. Only on a fresh
// registration's contact tab; view/edit and the special PAN/NRI/credit fields
// always show in full.
export const isTuckedField = (field, { newCustomer, contactTab, moreDetails, isPanCustomer, isNriBilling, isCreditSale }) => {
  if (!newCustomer || !contactTab || moreDetails) return false;
  if (isEssentialField(field)) return false;
  const fn = String(field?.name || "")
    .toLowerCase()
    .replace(/\s+/g, "");
  if (isPanCustomer && fn.includes("pan")) return false;
  if (isNriBilling && (fn.startsWith("passport") || fn.startsWith("visa") || fn.startsWith("immigration"))) return false;
  if (isCreditSale && fn.startsWith("credit")) return false;
  return true;
};

export const fieldKeyOf = (field) => toCamelCase(field?.name);
